import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useAuth } from "../../accounts/AuthProvider";
import { useSavedCharts } from "../../accounts/SavedChartsProvider";
import { chartErrorMessage, getChart } from "../../accounts/chartRepository";
import { mainActions } from "../../store";
import PublishScalePage from "../PublishScalePage/PublishScalePage";
import classes from "./LibraryPage.module.css";

const PrivateLibraryChart = ({ id }) => {
  const auth = useAuth();
  const saved = useSavedCharts();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const owner = auth.session?.user.id;
  const [state, setState] = useState(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!owner || !auth.client || auth.error) return;
    const controller = new AbortController();
    setState(null);
    getChart({ client: auth.client, accessToken: auth.session.access_token, signal: controller.signal }, id)
      .then(result => {
        if (controller.signal.aborted) return;
        dispatch(mainActions.loadChartDocument(result.document));
        setState({ id, owner, title: result.document.chart.title, error: "" });
      }).catch(error => {
        if (!controller.signal.aborted) setState({ id, owner, error: chartErrorMessage(error) });
      });
    return () => controller.abort();
  }, [id, owner, auth.client, auth.session?.access_token, auth.error, dispatch, retry]);
  const editController = useRef(null);
  useEffect(() => () => editController.current?.abort(), []);
  const edit = async () => {
    editController.current?.abort();
    const controller = new AbortController();
    editController.current = controller;
    const result = await saved.open(id, controller.signal);
    if (result && !controller.signal.aborted) navigate("/");
  };
  const current = state?.id === id && state.owner === owner ? state : null;
  return <>
    <HelmetProvider><Helmet><title>{current?.title || "Private chart"} | Guitar Charts</title>
      <meta name="robots" content="noindex, nofollow" /></Helmet></HelmetProvider>
    {auth.loading ? <p role="status">Checking your account…</p>
      : !owner || auth.error ? <p><Link to="/account">Sign in</Link> to view your private chart.</p>
      : !current ? <p role="status">Loading your chart…</p>
      : current.error ? <div role="alert"><p>{current.error}</p><button onClick={() => setRetry(value => value + 1)}>Retry chart</button></div>
      : <>
        <div className={classes.privateActions}>
          <span>Private · Only you can see this chart</span>
          <button disabled={saved.busy} onClick={edit}>Edit chart</button>
          <Link to="/my-charts">Manage My Charts</Link>
          {saved.error && <p role="alert">{saved.error}</p>}
        </div>
        <div className={classes.publishBorder}><PublishScalePage onClick={edit} manageMetadata={false} /></div>
      </>}
  </>;
};

export default PrivateLibraryChart;
